import React from "react";
import { Menu } from "lucide-react";
import { FaCamera, FaImage } from "react-icons/fa";
import AppBackButton from "../common/AppBackButton";

import API_BASE from "../../api";
import ProfileProgressionCard from "../ProfileProgressionCard";

function normalizeProfileCoverPosition(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 50;
  return Math.min(100, Math.max(0, parsed));
}

function normalizeProfileAvatarPosition(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 50;
  return Math.min(100, Math.max(0, parsed));
}

function resolveProfileAvatarObjectPosition(targetUser) {
  const positionX = normalizeProfileAvatarPosition(targetUser?.avatarPositionX);
  const positionY = normalizeProfileAvatarPosition(targetUser?.avatarPositionY);
  return `${positionX}% ${positionY}%`;
}

function resolveProfileCoverBackgroundPosition(targetUser) {
  const positionX = normalizeProfileCoverPosition(targetUser?.coverPositionX);
  const positionY = normalizeProfileCoverPosition(targetUser?.coverPositionY);
  return `${100 - positionX}% ${100 - positionY}%`;
}

export default function ProfileHeaderSection({
  profile,
  bioEditRequest = null,
  isBioEditRequestReady = false,
  onBioEditRequestConsumed,
  onOpenSharedSidebar,
}) {
  const {
    t,
    user,
    directoryUsers,
    isOwnProfile,
    backLabel,
    showMyCatchesButton,
    showCoverEdit,
    avatarPreviewUrl,
    coverPreviewUrl,
    mobileBurgerRef,
    avatarInputRef,
    coverInputRef,
    profileHeaderRef,
    handleAvatarInputChange,
    handleCoverInputChange,
    handleCoverEditInputChange,
    handleOpenAvatarFullscreen,
    handleOpenCoverFullscreen,
    handleCloseCoverEdit,
    handleCloseProfile,
    handleOpenCatches,
  } = profile;

  const handleAvatarCameraClick = (event) => {
    event.stopPropagation();
    avatarInputRef.current?.click();
  };

  const handleCoverCameraClick = (event) => {
    event.stopPropagation();
    coverInputRef.current?.click();
  };

  return (
    <>
      <input
        ref={avatarInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleAvatarInputChange}
      />

      <input
        ref={coverInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleCoverInputChange}
      />

      <div className="profile-mobile-topchrome rp-mobile-topchrome">
        <div className="profile-mobile-navbar rp-navbar">
          <button
            ref={mobileBurgerRef}
            type="button"
            className="profile-mobile-topchrome__menu rp-navbar__menu-btn mobile-menu-btn"
            aria-label={t("open_menu", { defaultValue: "Open menu" })}
            onClick={onOpenSharedSidebar}
          >
            <Menu size={23} strokeWidth={2.25} aria-hidden="true" />
          </button>

          <div className="profile-mobile-topchrome__logo rp-navbar__logo">
            <img
              src="/banner/logo.png"
              alt={t("app_title")}
              className="profile-mobile-topchrome__logo-img rp-navbar__logo-img"
            />
            <div className="profile-mobile-topchrome__wordmark profile-mobile-topchrome__username rp-navbar__wordmark">
              {user?.username || t("app_title")}
            </div>
          </div>
        </div>
      </div>

      <div className="profile-header-wrapper">
        <div
          onClick={handleOpenAvatarFullscreen}
          className="profile-avatar-wrapper"
        >
          <div className="profile-avatar-frame">
            <img
              src={
                avatarPreviewUrl ||
                (user?.avatar
                  ? `${API_BASE}${user.avatar}?t=${user.updatedAt || Date.now()}`
                  : "https://cdn-icons-png.flaticon.com/512/847/847969.png")
              }
              alt={t("profile_picture")}
              className="profile-avatar"
              style={{
                objectPosition: resolveProfileAvatarObjectPosition(user),
                transform: `scale(${user?.avatarScale || 1})`,
              }}
            />
          </div>

          {isOwnProfile && (
            <button
              type="button"
              className="profile-avatar-camera-btn"
              aria-label={t("profile_change_photo_label", {
                defaultValue: "Change profile photo",
              })}
              onClick={handleAvatarCameraClick}
            >
              <FaCamera aria-hidden="true" />
            </button>
          )}
        </div>

        <h2 className="profile-floating-name">{user?.username}</h2>

        <div
          ref={profileHeaderRef}
          className={`profile-header ${coverPreviewUrl || user?.cover ? "has-cover" : ""}`}
          style={
            coverPreviewUrl || user?.cover
              ? {
                  backgroundImage: `url(${coverPreviewUrl || `${API_BASE}${user.cover}?t=${user.updatedAt || Date.now()}`})`,
                  backgroundPosition: resolveProfileCoverBackgroundPosition(user),
                  backgroundSize: `${100 * (user?.coverScale || 1)}%`,
                }
              : undefined
          }
          onClick={handleOpenCoverFullscreen}
        >
          {isOwnProfile && (
            <button
              type="button"
              className="profile-cover-camera-btn"
              aria-label={t("profile_change_cover_label", {
                defaultValue: "Change cover photo",
              })}
              onClick={handleCoverCameraClick}
            >
              <FaCamera aria-hidden="true" />
              <span>
                {t("profile_edit_cover_photo", { defaultValue: "Edit cover photo" })}
              </span>
            </button>
          )}

          {showCoverEdit && (
            <div
              className="profile-popup cover-popup"
              onClick={(event) => event.stopPropagation()}
            >
              <h3 className="popup-title">{t("update_cover_image")}</h3>

              <div className="popup-row">
                <input
                  ref={coverInputRef}
                  type="file"
                  accept="image/*"
                  className="popup-hidden-input"
                  onChange={handleCoverEditInputChange}
                />

                <button
                  type="button"
                  className="popup-add-photo-btn"
                  onClick={() => coverInputRef.current?.click()}
                >
                  <FaImage /> {t("add_photo")}
                </button>

                <button
                  className="close-popup-btn"
                  onClick={handleCloseCoverEdit}
                >
                  {t("cancel")}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="profile-identity">
          <ProfileProgressionCard
            progression={user?.progression}
            profileDetails={user}
            directoryUsers={directoryUsers}
            className="profile-progression-card--own"
            isOwnProfile={true}
            bioEditRequest={bioEditRequest}
            isBioEditRequestReady={isBioEditRequestReady}
            onBioEditRequestConsumed={onBioEditRequestConsumed}
          />
        </div>
      </div>

      <div className="profile-sidebar">
        <AppBackButton
          className="profile-sidebar-back-btn"
          label={backLabel}
          onClick={handleCloseProfile}
        />

        {showMyCatchesButton && isOwnProfile && (
          <button className="profile-btn catches-btn" onClick={handleOpenCatches}>
            {'\u{1F3A3}'} {t("my_catches")}
          </button>
        )}
      </div>
    </>
  );
}
